import { useEffect, useRef, useState } from 'react';
import { Button, Card, SpinLoading, Toast } from 'antd-mobile';
import { CheckCircleOutline, CloseCircleOutline } from 'antd-mobile-icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { claimStoreOpeningOffer } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './StoreOpeningClaimPage.css';

/** 固定链接：https://m.taogo.shop/claim/store-opening?token=数据库中保存的固定 token */
export default function StoreOpeningClaimPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token: loginToken, initialized, isSessionReady, isAuthenticated, logoutLocalOnly } = useAuth();
  const [result, setResult] = useState(null);
  const claimKeyRef = useRef(null);
  const offerToken = searchParams.get('token')?.trim() || '';

  useEffect(() => {
    if (!initialized || !isSessionReady) return;
    if (!offerToken) {
      setResult({ ok: false, title: 'Invalid offer link', message: 'This offer link is incomplete.' });
      return;
    }
    if (!isAuthenticated || !user?.id || !loginToken) {
      const returnPath = `${location.pathname}${location.search}`;
      if (isAuthenticated && !loginToken) logoutLocalOnly();
      Toast.show({ content: 'Please sign in or register to claim this offer.' });
      navigate(`/login?redirect=${encodeURIComponent(returnPath)}`, { replace: true });
      return;
    }

    const claimKey = `${user.id}:${offerToken}`;
    if (claimKeyRef.current === claimKey) return;
    claimKeyRef.current = claimKey;

    (async () => {
      try {
        const data = await claimStoreOpeningOffer(offerToken, loginToken);
        const newlyClaimed = data?.newlyClaimed === true;
        setResult({
          ok: true,
          newlyClaimed,
          title: newlyClaimed ? 'Offer claimed!' : 'Already claimed',
          message: data?.message || (newlyClaimed
            ? 'Your TaoGo 19 Opening Offer has been added to your account.'
            : 'You have already claimed this offer.'),
        });
        Toast.show({
          icon: 'success',
          content: newlyClaimed ? 'RM10 voucher received!' : 'You already have this offer.',
        });
      } catch (error) {
        if (error?.code === 'NOT_LOGIN') {
          logoutLocalOnly();
          const returnPath = `${location.pathname}${location.search}`;
          navigate(`/login?redirect=${encodeURIComponent(returnPath)}`, { replace: true });
          return;
        }
        setResult({
          ok: false,
          title: 'Could not claim',
          message: error?.message || 'This offer is currently unavailable.',
        });
      }
    })();
  }, [initialized, isSessionReady, isAuthenticated, user?.id, loginToken, offerToken, location.pathname, location.search, navigate, logoutLocalOnly]);

  if (!initialized || !isSessionReady || (!result && isAuthenticated)) {
    return (
      <div className="store-opening-claim-page">
        <div className="store-opening-claim-card loading">
          <SpinLoading color="currentColor" style={{ fontSize: 48 }} />
          <div>Claiming your TaoGo 19 offer…</div>
        </div>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="store-opening-claim-page">
      <div className="store-opening-confetti" aria-hidden="true">🎉 ✦ 🎊 ✦ 🎉</div>
      <Card className="store-opening-claim-card">
        {result.ok ? (
          <CheckCircleOutline className="store-opening-claim-icon success" />
        ) : (
          <CloseCircleOutline className="store-opening-claim-icon failure" />
        )}
        <div className="store-opening-claim-kicker">TAOGO 19 OPENING OFFER</div>
        <div className="store-opening-claim-title">{result.title}</div>
        {result.ok && result.newlyClaimed && (
          <div className="store-opening-claim-value"><span>RM</span>10 OFF</div>
        )}
        <div className="store-opening-claim-message">{result.message}</div>
        {result.ok && (
          <div className="store-opening-claim-terms">
            Minimum spend RM28 · Valid at TaoGo 19 only<br />
            Effective tomorrow · Valid for 30 days
          </div>
        )}
        <Button block className="store-opening-claim-button" onClick={() => navigate('/', { replace: true })}>
          View my vouchers
        </Button>
      </Card>
    </div>
  );
}
